import { LinearProgress } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { ReactKeycloakProvider } from '@react-keycloak/web'
import useAPIFetch from '@hooks/useAPIFetch'
import useLocalStorage from '@hooks/useLocalStorage'
import UserService from '@services/users/user.service'
import { InitUserInfo, UserInfo } from '@models/user.model'
import { LandingPageProjectInfo } from '@models/projects.model'
import { InitMyOrganizationInfo, MyOrganizationInfo } from '@models/organization.model'
import keycloak from '../../keycloak'
import { AuthContext } from './provider'
import { useNavigate } from 'react-router-dom'

interface Props {
	children: React.ReactNode
}

interface Tokens {
	idToken?: string
	refreshToken?: string
	token?: string
}

const KeycloakWrapper: React.FC<Props> = ({ children }) => {
	const { state: user, dispatch } = useContext(AuthContext)

	const { setRequest, isLoading } = useAPIFetch()
	const [, setUser] = useLocalStorage<string | undefined>('user', '')
	const [, setToken] = useLocalStorage<string | undefined>('token', '')
	const [, setIsAdmin] = useLocalStorage<string>('admin', '')
	const navigate = useNavigate()
	const [logoutState, setLogoutState] = useState(false)

	useEffect(() => {
		if (logoutState) {
			setTimeout(() => {
				navigate(`/session-timeout?redirectURL=${encodeURI(window.location.href)}`, {
					replace: true,
				})
			}, 1000)

			console.debug('navigate to session-timeout page')
		}
	}, [logoutState])

	const getUserGeneralInfo = () => {
		setRequest({
			callback: async (_: any) => {
				await UserService.CheckProfile()

				const userInfo: UserInfo = await UserService.GetUserInfo().catch((error) => {
					console.error('error', error.message)
					return InitUserInfo
				})
				const organizationInfo: MyOrganizationInfo = await UserService.GetUserOrganization()
					.then((data) => {
						//TODO: delete this later
						data.map_config.color_mapping = [
							{ key: 'cde', value: '#D77ECA' },
							{ key: 'ssss', value: '#4BBE55' },
							{ key: 'twin', value: '#5D7DC1' },
							{ key: 'dwss', value: '#2891A0' },
							{ key: 'finance', value: '#96610D' },
						]
						return data
					})
					.catch((error) => {
						console.error('error', error.message)
						return InitMyOrganizationInfo
					})
				const landingPageProjects: LandingPageProjectInfo[] =
					await UserService.GetUserLandingProjects().then((result) => result.projects)

				dispatch({
					type: 'login',
					userInfo: userInfo,
					organizationInfo,
					landingPageProjects,
					completedInitiated: true,
				})

				setUser(JSON.stringify(userInfo))
				setIsAdmin(String(userInfo.is_super_admin || false))
			},
		})
	}

	const eventLogger = (event: string, error: unknown) => {
		console.debug('[Keycloak Event]', event)

		console.debug('[Keycloak Error]', error)
		if (error) {
			console.error('[Keycloak Error]', error)
		}

		switch (event) {
			case 'onReady':
				if (
					!keycloak.authenticated &&
					!logoutState &&
					!window.location.href.includes('/session-timeout')
				)
					keycloak.login()
				break
			case 'onAuthError':
				break
			case 'onAuthRefreshError':
			case 'onAuthLogout':
				setLogoutState(true)
				// UserService.CreateAuditTrail({
				// 	date: new Date().toISOString(),
				// 	message: 'Logout system',
				// 	eventType: 'LOGOUT',
				// 	status: 'SUCCESS',
				// })
				break
			case 'onAuthSuccess':
				setToken(keycloak.token)
				// UserService.CreateAuditTrail({
				// 	date: new Date().toISOString(),
				// 	message: 'Login system',
				// 	eventType: 'LOGIN',
				// 	status: 'SUCCESS',
				// })
				break
			default:
				break
		}
	}

	const tokenLogger = (tokens: Tokens) => {
		if (tokens?.token) {
			setToken(tokens.token)
			getUserGeneralInfo()
			setLogoutState(false)
		}
	}

	return (
		<ReactKeycloakProvider authClient={keycloak} onEvent={eventLogger} onTokens={tokenLogger}>
			{isLoading ? <LinearProgress /> : children}
		</ReactKeycloakProvider>
	)
}

export default KeycloakWrapper
